import { useMemo } from 'react';

import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { ParsedUniqueId, safeParseUniqueId } from '@/typings/parseUniqueId';
import { truthy } from '@/util/array';
import { useAppliedLineage } from './dbtStrategy/useAppliedLineage';
import { DbtResponse } from './discovery/types';

export const useChildren = (
  resource: DbtResponse<Partial<any>, any>,
): ParsedUniqueId[] | undefined => {
  const data = resource.data;
  return useMemo(() => {
    if (!data) return undefined;
    if ('children' in data && data.children) {
      return data.children
        .map((child: any) =>
          child?.uniqueId ? safeParseUniqueId(child.uniqueId).ok : null,
        )
        .filter(truthy);
    }
    return undefined;
  }, [data]);
};

export const useParents = (
  uniqueId: string | undefined,
): ParsedUniqueId[] | undefined => {
  const { environmentId } = useDbtCloudProject();
  const lineageQuery = useAppliedLineage({
    environmentId,
    uniqueIds: [uniqueId!],
    pause: !uniqueId,
  });
  const lineage = lineageQuery.data?.environment.applied.lineage;
  return useMemo(() => {
    if (!uniqueId) return undefined;
    const match = lineage?.find((node) => node.uniqueId === uniqueId);
    if (!match) return undefined;
    if ('parentIds' in match) {
      return match.parentIds
        .sort()
        .map((uniqueId) => safeParseUniqueId(uniqueId).ok)
        .filter(truthy);
    }
    return [];
  }, [uniqueId, lineage]);
};
